import {Link} from 'react-router-dom';
import {NotificationDropdown} from '../components/notifications/NotificationDropdown';
import {ProfileMenu} from './ProfileMenu';
import {Button} from '../components/Button';
import {ErrorBoundary} from '../components/errorBoundary';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  iconSolid: React.ComponentType<{ className?: string }>;
}

interface DesktopHeaderProps {
  profile:
    | {
        username: string;
        userId: string;
      }
    | undefined;
  navigationItems: NavigationItem[];
  isActive: (path: string) => boolean;
  navigateToLogin: (showSignup?: boolean) => void;
  profileImageUrl: string;
  onImageError: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  isLoading: boolean;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Desktop header navigation component.
 * Displays navigation links, notification dropdown, and profile menu.
 * Shows a loading state while authentication is being resolved.
 */
export const DesktopHeader = ({
  profile,
  navigationItems,
  isActive,
  navigateToLogin,
  profileImageUrl,
  onImageError,
  isLoading,
}: DesktopHeaderProps) => {
  return (
    <div className="sticky top-0 z-40 flex-shrink-0 flex h-16 shadow bg-subtle">
      <div className="flex-1 px-4 flex ml-4 md:ml-6 items-center justify-between">
        {/* Desktop Navigation - Left Side - Only show when logged in */}
        {profile ? (
          <div className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={classNames(
                  isActive(item.href)
                    ? 'text-action-primary '
                    : 'text-main hover:text-action-primary-hover',
                  'text-base  transition-colors duration-200',
                )}
              >
                {item.name}
              </Link>
            ))}
          </div>
        ) : (
          /* Empty div to maintain layout when not logged in */
          <div className="hidden md:block"></div>
        )}

        {/* Logo - Center */}
        <div className="absolute left-1/2 transform -translate-x-1/2">
          <Link to={profile ? '/home' : '/'}>
            <img src="/logo_side_full_big.png" alt="TripVibes" className="h-10" />
          </Link>
        </div>

        {/* Right Side */}
        <div className="ml-auto flex items-center gap-4">
          {isLoading ? (
            /* Loading state - show nothing to prevent flickering */
            <div className="hidden md:block w-24"></div>
          ) : profile ? (
            <div className="hidden md:flex items-center gap-4">
              {/* Notification Dropdown */}
              <ErrorBoundary>
                <NotificationDropdown />
              </ErrorBoundary>

              {/* Profile Menu */}
              <ProfileMenu
                profile={profile}
                profileImageUrl={profileImageUrl}
                onImageError={onImageError}
              />
            </div>
          ) : (
            /* Login/Register Button for non-logged-in users */
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigateToLogin(true)}
              className="bshadow-md  px-3 py-2 md:px-4"
            >
              Register
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
